/**
 * Amplitude wiring for the marketing site. This is the site's only analytics
 * destination now — Segment's analytics.js is gone, and `segmentTrack` (kept
 * under its old name so the ~30 call sites stay put) forwards here and to the
 * GTM dataLayer. See src/functions/segment.ts.
 */
import type * as AmplitudeSDK from '@amplitude/analytics-browser'

type Amplitude = typeof AmplitudeSDK

/**
 * Amplitude browser API key for the project this build reports to. Public by
 * design — Next inlines every `NEXT_PUBLIC_*` var into the client bundle, and a
 * browser key is write-only (it can send events, it cannot read any back). Never
 * put an Amplitude *secret* key or API secret in a `NEXT_PUBLIC_*` var.
 */
export const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? ''

/**
 * Amplitude has a single "Production" project, and the key lives only in
 * `.env.production` so `yarn dev` sends nothing. That is not enough on its own:
 * Next loads `.env.production` for `next build` *and* `next start`, so a local
 * production build would report real events into the one project we have. Bail
 * out on localhost, which is the only thing that distinguishes a local prod
 * build from a deployed one — `NEXT_PUBLIC_ENV` reads "production" in both.
 */
const isLocalHost = () => {
  if (typeof window === 'undefined') return false
  const { hostname } = window.location
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

/**
 * Durable per-visitor id, used as Amplitude's `device_id` *and* as the
 * `anonymousId` join key we hand to the n8n chat. Owning it ourselves rather
 * than reading Amplitude's own storage means it is available synchronously on
 * the very first render — the chatbot embed and the experiment exposure event
 * both need it before the SDK chunk has finished loading, which is exactly the
 * hole the old Segment `ajs_anonymous_id` lookup used to fall into.
 */
const ANONYMOUS_ID_KEY = 'dimension_anonymous_id'

let memoryAnonymousId = ''

const randomId = () => {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * The visitor's anonymous id, creating and persisting one on first call.
 * Returns "" on the server. In a private window or with site data blocked,
 * localStorage throws — the id then lives in memory for this page view only,
 * which is still better than an empty join key.
 */
export const getAnonymousId = (): string => {
  if (typeof window === 'undefined') return ''
  try {
    const stored = window.localStorage.getItem(ANONYMOUS_ID_KEY)
    if (stored) return stored
    const created = memoryAnonymousId || randomId()
    window.localStorage.setItem(ANONYMOUS_ID_KEY, created)
    return created
  } catch {
    if (!memoryAnonymousId) memoryAnonymousId = randomId()
    return memoryAnonymousId
  }
}

let sdk: Amplitude | null = null
let loading: Promise<Amplitude | null> | null = null
/**
 * Set once the SDK chunk has failed to load, so we stop retrying the import on
 * every subsequent event — for a visitor whose ad blocker eats the chunk, that
 * would be one doomed request per tracked interaction for the rest of the session.
 */
let unavailable = false
/**
 * Events fired before the SDK chunk resolved. Replayed in order on load, each
 * carrying the time it actually happened — without that, a queued event is
 * stamped at load time and can sort *after* events that really came later,
 * which would scramble the very funnel ordering these events exist to show.
 */
const pending: Array<{ name: string; properties?: Record<string, any>; time: number }> = []

/**
 * Loads and initializes Amplitude, once.
 *
 * The SDK is imported dynamically so its ~40 kB lands in a lazy chunk instead of
 * the initial JS of every page — the same Core Web Vitals reasoning as
 * `useExperimentVariant` and `N8nChatEmbed`, and it matters more here because
 * this module is reachable from every page.
 */
export const initAmplitude = (): Promise<Amplitude | null> => {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (sdk) return Promise.resolve(sdk)
  if (loading) return loading
  if (unavailable) return Promise.resolve(null)

  if (!AMPLITUDE_API_KEY || isLocalHost()) {
    // Nothing to send to. Drop anything queued so it can't leak memory on a long
    // session, and stay quiet — a missing key is a config problem, not a
    // per-event runtime error.
    unavailable = true
    pending.length = 0
    return Promise.resolve(null)
  }

  loading = import('@amplitude/analytics-browser')
    .then((amplitude) => {
      amplitude.init(AMPLITUDE_API_KEY, {
        deviceId: getAnonymousId(),
        autocapture: {
          // Page views (including Pages Router client-side navigations),
          // sessions and utm/referrer attribution come free and replace what
          // Segment's `analytics.page()` used to do.
          pageViews: true,
          sessions: true,
          attribution: true,
          // Explicit events only. Autocapturing every click and form field on a
          // 70-page marketing site buries the handful of events we actually
          // report on, and inflates event volume we pay for.
          elementInteractions: false,
          formInteractions: false,
          fileDownloads: false,
        },
      })
      sdk = amplitude
      pending.splice(0).forEach(({ name, properties, time }) => {
        amplitude.track(name, properties, { time })
      })
      amplitude.flush()
      return amplitude
    })
    .catch(() => {
      // Chunk blocked by an ad blocker, or offline. Analytics is never allowed to
      // break a page, so swallow it and give up for the rest of the session
      // rather than re-attempting the import on every event.
      unavailable = true
      pending.length = 0
      loading = null
      return null
    })

  return loading
}

/**
 * Sends one event, flushing immediately rather than waiting out the SDK's
 * batching interval.
 *
 * Almost every event on this site fires from a CTA that navigates away —
 * usually to app.getdimension.com, a different origin — so a batch still
 * sitting in the queue when the document unloads is a lost conversion. The
 * fetch transport sets `keepalive`, so a request already in flight survives the
 * navigation; one that was never issued does not. Event volume here is low
 * enough that a request per event costs nothing.
 */
export const amplitudeTrack = (name: string, properties?: Record<string, any>) => {
  if (typeof window === 'undefined' || unavailable) return
  if (!sdk) {
    pending.push({ name, properties, time: Date.now() })
    initAmplitude()
    return
  }
  sdk.track(name, properties)
  sdk.flush()
}
